import { t } from '@bia-auction/shared-i18n';
import type { SaleStats, SaleSummary } from '@bia-auction/shared-schema';
import {
  paginatedSchema,
  saleStatsSchema,
  saleSummarySchema,
} from '@bia-auction/shared-schema';
import {
  Card,
  CardContent,
  EmptyState,
  PageHeader,
  SkeletonRows,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  saleStatusVariant,
} from '@bia-auction/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Gavel } from 'lucide-react';
import { ErrorMsg } from '../components/Feedback';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatCount, formatDate, formatKurus } from '../lib/format';
import { presentationLabel } from '../lib/labels';
import { can } from '../lib/roles';

/**
 * Admin home ("Genel Bakış") — the answer to "kaç ihale aktif, kaçı pasif, ne satıldı, ne
 * kadar" (Bia feedback, 07.08.2026). Landing on the sale *list* made an operator count rows
 * across three tabs to learn something the server can answer in one query.
 *
 * Two deliberate limits:
 * - **Counters, not analytics.** Conversion rates / top bidders / deposit-to-bid ratios are
 *   the P2 analytics dashboard (`docs/01-prd.md` non-goals) and stay out.
 * - **Sold value is per currency.** `GET /admin/sales/stats` returns one row per lot
 *   currency; merging them into a single figure would invent a number.
 *
 * Every figure is firm-scoped server-side, so a firm operator's overview totals exactly the
 * auctions their list would show.
 */

const salesPageSchema = paginatedSchema(saleSummarySchema);

export function DashboardPage() {
  const { state } = useAuth();
  const stats = useQuery({
    queryKey: ['sale-stats'],
    queryFn: () => api('/admin/sales/stats', {}, saleStatsSchema),
  });
  // The running auctions themselves — the list endpoint already returns them newest-first, so
  // the home page links straight into the live console instead of describing a count.
  const running = useQuery({
    queryKey: ['sales', 'future', '', 0, 'dashboard'],
    queryFn: () => api('/admin/sales?state=future&limit=25&offset=0', {}, salesPageSchema),
  });

  return (
    <div className="space-y-6">
      <PageHeader title={t('dashboard.title')} />

      {stats.isLoading ? (
        <SkeletonRows rows={3} />
      ) : stats.isError ? (
        <ErrorMsg error={stats.error} />
      ) : stats.data ? (
        <StatsSections stats={stats.data} />
      ) : null}

      <section className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-head text-h3 font-semibold">{t('dashboard.live.title')}</h2>
          <Link to="/sales" className="text-sm text-tx-brand hover:underline">
            {t('dashboard.viewAll')}
          </Link>
        </div>
        {running.isLoading ? (
          <SkeletonRows rows={3} />
        ) : running.isError ? (
          <ErrorMsg error={running.error} />
        ) : (
          <RunningTable
            items={(running.data?.items ?? []).filter(isRunning)}
            linkable={can(state.roles, 'salesView')}
          />
        )}
      </section>
    </div>
  );
}

/** LIVE and PAUSED are both "running now" — a paused auction is mid-flight, not finished. */
const isRunning = (s: SaleSummary): boolean => s.state === 'LIVE' || s.state === 'PAUSED';

function StatsSections({ stats }: { stats: SaleStats }) {
  const sale = (k: keyof SaleStats['sales']): number => stats.sales[k] ?? 0;
  const lot = (k: keyof SaleStats['lots']): number => stats.lots[k] ?? 0;

  // "Aktif" = anything an operator still has to run (published/live/paused); "pasif" =
  // finished business (closed/cancelled). Drafts are neither — they were never published, so
  // they get their own tile rather than inflating either side.
  const active = sale('PUBLISHED') + sale('LIVE') + sale('PAUSED');
  const passive = sale('CLOSED') + sale('CANCELLED');
  const totalLots = lot('SCHEDULED') + lot('OPEN') + lot('SOLD') + lot('UNSOLD') + lot('REMOVED');

  return (
    <>
      <section className="space-y-2">
        <h2 className="font-head text-h3 font-semibold">{t('dashboard.section.sales')}</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Stat
            label={t('dashboard.sales.active')}
            hint={t('dashboard.sales.activeHint')}
            value={active}
            parts={[
              [t('dashboard.sales.live'), sale('LIVE')],
              [t('dashboard.sales.paused'), sale('PAUSED')],
              [t('dashboard.sales.published'), sale('PUBLISHED')],
            ]}
          />
          <Stat
            label={t('dashboard.sales.passive')}
            hint={t('dashboard.sales.passiveHint')}
            value={passive}
            parts={[
              [t('dashboard.sales.closed'), sale('CLOSED')],
              [t('dashboard.sales.cancelled'), sale('CANCELLED')],
            ]}
          />
          <Stat label={t('dashboard.sales.draft')} value={sale('DRAFT')} />
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="font-head text-h3 font-semibold">{t('dashboard.section.lots')}</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label={t('dashboard.lots.total')} value={totalLots} />
          <Stat label={t('dashboard.lots.sold')} value={lot('SOLD')} />
          <Stat label={t('dashboard.lots.unsold')} value={lot('UNSOLD')} />
          <Stat
            label={t('dashboard.lots.open')}
            value={lot('OPEN')}
            parts={[[t('dashboard.lots.scheduled'), lot('SCHEDULED')]]}
          />
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="font-head text-h3 font-semibold">{t('dashboard.section.sold')}</h2>
        {stats.sold.length === 0 ? (
          <Card>
            <CardContent className="p-5 text-sm text-muted-foreground">
              {t('dashboard.sold.empty')}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {stats.sold.map((row) => (
              <Card key={row.currency}>
                <CardContent className="space-y-1 p-5">
                  <div className="font-head text-display font-semibold tabular-nums">
                    {formatKurus(row.amount, row.currency)}
                  </div>
                  <div className="text-sm text-muted-foreground tabular-nums">
                    {t('dashboard.sold.count', { n: formatCount(row.count) })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

/** One counter tile: the headline figure, plus an optional breakdown of what it adds up from. */
function Stat({
  label,
  hint,
  value,
  parts,
}: {
  label: string;
  hint?: string;
  value: number;
  parts?: Array<[string, number]>;
}) {
  return (
    <Card>
      <CardContent className="space-y-1 p-5">
        <div className="text-sm text-muted-foreground">{label}</div>
        <div className="font-head text-display font-semibold tabular-nums">
          {formatCount(value)}
        </div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
        {parts && parts.length > 0 && (
          <ul className="flex flex-wrap gap-x-3 gap-y-0.5 pt-1 text-xs text-muted-foreground">
            {parts.map(([name, n]) => (
              <li key={name} className="tabular-nums">
                {name}: {formatCount(n)}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/** `linkable` mirrors the list page: FIRM_VENDOR may see a sale in the list but
 * not open its detail, so they must not be linked to a guaranteed 403. */
function RunningTable({ items, linkable }: { items: SaleSummary[]; linkable: boolean }) {
  if (items.length === 0) return <EmptyState icon={Gavel} title={t('dashboard.live.empty')} />;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('sales.col.code')}</TableHead>
          <TableHead>{t('sales.col.title')}</TableHead>
          <TableHead>{t('sales.col.start')}</TableHead>
          <TableHead>{t('sales.col.lots')}</TableHead>
          <TableHead>{t('sales.col.status')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((s) => (
          <TableRow key={s.id}>
            <TableCell className="font-mono text-xs">{s.code}</TableCell>
            <TableCell>
              {linkable ? (
                <Link
                  to="/sales/$saleId"
                  params={{ saleId: s.id }}
                  className="text-tx-brand hover:underline"
                >
                  {s.title ?? t('sales.untitled')}
                </Link>
              ) : (
                <span>{s.title ?? t('sales.untitled')}</span>
              )}
            </TableCell>
            <TableCell className="tabular-nums">{formatDate(s.startDate)}</TableCell>
            <TableCell className="tabular-nums">{s.lotCount}</TableCell>
            <TableCell>
              <StatusBadge variant={saleStatusVariant(s.state)}>
                {presentationLabel(s.presentationStatus)}
              </StatusBadge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
